import { RefObject } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Color from '@arcgis/core/Color.js';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import LayerList from '@arcgis/core/widgets/LayerList';
import Fullscreen from '@arcgis/core/widgets/Fullscreen';
import ImageryLayer from '@arcgis/core/layers/ImageryLayer';
import { LAYERS_CONFIG } from '../config/layer.config';
import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import Graphic from '@arcgis/core/Graphic';
import Field from '@arcgis/core/layers/support/Field';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { LOG_REPORT_LAYER_URL } from '../config/arcgis.config';
import genusTableController, { OperationType } from './genusTableController';
import { queryFeatures } from '../services/esri';

const { FOREST_PRESENCE, REFERENCE_DATA, GENUS_DISTRIBUTION, GENUS_RANGE } = LAYERS_CONFIG;

export interface ILogRecordAttributes {
  UploadDate: string;
  OrderNumber?: string;
  UserName: string;
  FeatureService?: string;
  GeoJson?: string;
  UniqueID?: string;
  ReportID?: string;
  CertificationStatus?: string;
}

export interface ILogRecord {
  logAttributes: ILogRecordAttributes;
  type: OperationType;
}

const greenPolygonSymbol = new SimpleFillSymbol({
  color: [0, 128, 0, 0.5],
  outline: {
    color: [0, 128, 0, 0],
    width: 1,
  },
});

const redPolygonSymbol = new SimpleFillSymbol({
  color: [255, 0, 0, 0.5],
  outline: {
    color: [255, 0, 0, 0],
    width: 1,
  },
});

// Create a ClassBreaksRenderer to apply symbols based on probability values
const pointRenderer = new ClassBreaksRenderer({
  field: 'probability',
  classBreakInfos: [
    {
      minValue: 0,
      maxValue: 0.049999999,
      symbol: redPolygonSymbol,
      label: '< 0.05',
    },
    {
      minValue: 0.05,
      maxValue: Infinity,
      symbol: greenPolygonSymbol,
      label: '>= 0.05',
    },
  ],
});

class MapControllerVerification {
  map: Map | null = null;
  mapView: MapView | null = null;

  private logReportLayer: FeatureLayer | null;

  constructor() {
    this.logReportLayer = null;
  }

  getLogReportLayer = () => this.logReportLayer;

  initializeMap = async (domRef: RefObject<HTMLDivElement | null>, fullScreenDiv: RefObject<HTMLDivElement | null>) => {
    if (!domRef.current) return;

    this.map = new Map({
      basemap: 'topo',
    });

    this.mapView = new MapView({
      map: this.map,
      container: domRef.current,
      constraints: {
        minZoom: 3,
      } as any,
      center: [20.80389, 47.75838],
      zoom: 3,

      highlightOptions: {
        color: Color.fromString('orange'),
      } as any,
    });

    this.mapView.when(() => {
      if (!this?.mapView) return;
      const layerList = new LayerList({
        view: this?.mapView,
      });

      this.mapView.ui.add(layerList, 'top-right');
      if (!fullScreenDiv.current) return;
      this.mapView.ui.add(
        new Fullscreen({
          view: this.mapView,
          element: fullScreenDiv.current,
        }),
        'top-left'
      );
    });
  };

  addForestPresenceLayer = () => {
    const forestPresenceLayer = new ImageryLayer({
      ...FOREST_PRESENCE,
    });

    this.mapView?.map.add(forestPresenceLayer, this.mapView.map.layers.length - 1);
  };

  addReferenceDataLayer = (defExpression: string) => {
    const sentinelLayer = new FeatureLayer({
      ...REFERENCE_DATA,
      definitionExpression: `Analyzed = 'analyzed' ${defExpression}`,
    });

    this.mapView?.map.add(sentinelLayer);
  };

  addGenusLayer = (genusName: string) => {
    const genusDistributionLayer = new FeatureLayer({
      ...GENUS_DISTRIBUTION,
      url: GENUS_DISTRIBUTION.url(genusName),
    });
    this.mapView?.map.add(genusDistributionLayer, this.mapView.map.layers.length - 1);
  };

  queryLayer = async (layerID: string) => {
    const layer = this.map?.findLayerById(layerID) as __esri.FeatureLayer | null;
    if (!layer) return null;

    try {
      const response = await layer.queryFeatures({ where: '1=1', outFields: ['*'], returnGeometry: true });
      if (response.features?.length) return response.features[0].geometry;
      return null;
    } catch (error) {
      console.error('Error querying layer:', error);
      return null;
    }
  };

  isCountryWithinDistribution = (countryFeature: any, distributions: any) => {
    const isWithin = geometryEngine.intersects(countryFeature, distributions);
    return isWithin;
  };

  addGenusRangeFeatureLayer = (genusName: string) => {
    const genusRangeFeatureLayer = new FeatureLayer({
      ...GENUS_RANGE,
      url: GENUS_RANGE.url(genusName),
    });
    this.mapView?.map.add(genusRangeFeatureLayer, this.mapView.map.layers.length - 1);
  };

  addLayer = (params: any) => {
    const { url, expression, title, queryExtent = false } = params;
    const featureLayer = new FeatureLayer({
      url,
      definitionExpression: expression,
      renderer: pointRenderer,
      id: title,
      title,
    });

    if (queryExtent) {
      featureLayer.queryExtent().then((response) => {
        this.mapView?.goTo({ target: response.extent }, { animate: true });
      });
    }
    this.map?.add(featureLayer);
  };

  removeLayerById = (id: string) => {
    const layer = this.map?.findLayerById(id);
    if (layer) {
      this.map?.remove(layer);
    }
  };

  toggleLayersVisibility = () => {
    const genusDistLayer = this.map?.findLayerById(GENUS_DISTRIBUTION.id);
    const genusRangeLayer = this.map?.findLayerById(GENUS_RANGE.id);
    const refernceLayer = this.map?.findLayerById(REFERENCE_DATA.id);
    const claimedHarvestOriginLayer = this.map?.findLayerById('claimed-harvest-origin-layer');

    if (genusDistLayer) {
      genusDistLayer.visible = !genusDistLayer.visible;
    }
    if (genusRangeLayer) {
      genusRangeLayer.visible = !genusRangeLayer.visible;
    }
    if (refernceLayer) {
      refernceLayer.visible = !refernceLayer.visible;
    }
    // Keep claimed harvest origin layer visible - don't toggle its visibility
    // We still need to find the layer to ensure it exists, but we don't toggle its visibility
    if (claimedHarvestOriginLayer) {
      // Keep it visible - don't toggle
      claimedHarvestOriginLayer.visible = true;
    }
  };

  addGeoJsonFileToMap = async (geojsonUrl: any) => {
    const geojsonLayer = new GeoJSONLayer({
      url: geojsonUrl,
    });
    await geojsonLayer.load();

    // Query features from the layer for return data (don't add as separate layer)
    const geoJSONFeature = await geojsonLayer.queryFeatures();
    
    return { geoJSONFeature, geojsonLayer };
  };

  addShapefileToMap = async (featureCollectionPromise: any) => {
    try {
      const featureCollection = await featureCollectionPromise;

      const fillSymbol = {
        type: 'simple-fill',
        color: [227, 139, 79, 0.8],
        outline: {
          color: [255, 255, 255],
          width: 1,
        },
      } as any;

      const renderer = new SimpleRenderer({
        symbol: fillSymbol,
      });

      const shapeFileFeatureLayers = featureCollection.layers.map((layer: any) => {
        const graphics = layer.featureSet.features
          .map((feature: any) => {
            let graphic: Graphic | null = null;
            if (typeof feature === 'object' && feature !== null) {
              graphic = Graphic.fromJSON(feature);
            }
            return graphic;
          })
          .filter((graphic: Graphic | null) => graphic !== null) as Graphic[];

        const featureLayer = new FeatureLayer({
          objectIdField: 'FID',
          source: graphics,
          fields: layer.layerDefinition.fields.map((field: any) => {
            return Field.fromJSON(field);
          }),
          renderer: renderer,
        });

        return featureLayer;
      });

      if (shapeFileFeatureLayers && shapeFileFeatureLayers.length) {
        this.mapView?.map.addMany(shapeFileFeatureLayers);
        
        // Navigate to the first layer's extent
        if (shapeFileFeatureLayers[0]) {
          shapeFileFeatureLayers[0].queryExtent().then((response: any) => {
            this.mapView?.goTo({ target: response.extent }, { animate: true });
          });
        }
      }
    } catch (error) {
      console.error('Error adding polygon to map:', error);
    }
  };

  loadLogReportLayer = async () => {
    this.logReportLayer = new FeatureLayer({
      url: LOG_REPORT_LAYER_URL,
    });
    await this.logReportLayer.load();
    return this.logReportLayer;
  };

  createReportLogRecord = async (params: ILogRecord) => {
    const { logAttributes, type } = params;
    const logLayer = await mapControllerVerification.loadLogReportLayer();
    try {
      const response = await genusTableController.addFeatureData({
        layer: logLayer,
        features: [{ attributes: logAttributes }],
        type,
      });

      if (response.success) {
        return true;
      }
      return false;
    } catch (err) {
      console.log('err', err);
      return false;
    }
  };

  getCreatedLogRecord = async (whereClause: string) => {
    const getRecordResponse = await queryFeatures(
      {
        where: whereClause,
        outFields: ['*'],
        returnGeometry: false,
      },
      `${LOG_REPORT_LAYER_URL}/0`
    );
    return getRecordResponse;
  };

  geMapScreenshot = async () => {
    const scale = 3;
    const result = await this.mapView?.takeScreenshot({
      width: 490 * scale,
      height: 250 * scale,
    });
    return result?.dataUrl;
  };

  getspatialReference = () => {
    return this.mapView?.spatialReference;
  };

  removeAllLayers = () => {
    this.mapView?.map.removeAll();
  };

  destroyMap = () => {
    if (this.mapView) {
      this.mapView.destroy();
    }
  };

  addClaimedHarvestOriginLayer = async (geometry: any, title: string = 'Claimed Harvest Origin') => {
    // Remove any existing claimed harvest origin layer
    this.removeClaimedHarvestOriginLayer();

    // Create a fill symbol for the claimed harvest origin
    const fillSymbol = new SimpleFillSymbol({
      color: [255, 255, 255, 0], // White color with full transparency (essentially no fill)
      outline: {
        color: [0, 112, 255], // Blue outline
        width: 2,
      },
    });

    // Create a renderer for the layer
    const renderer = new SimpleRenderer({
      symbol: fillSymbol,
    });

    // Create a feature layer from the geometry
    const featureLayer = new FeatureLayer({
      title: title,
      id: 'claimed-harvest-origin-layer',
      objectIdField: 'OBJECTID',
      source: [new Graphic({ 
        geometry: geometry,
        attributes: { OBJECTID: 1 }
      })],
      fields: [
        Field.fromJSON({
          name: 'OBJECTID',
          type: 'oid',
          alias: 'OBJECTID'
        })
      ],
      renderer: renderer,
    });

    // Add the layer to the map at the top position (index 0)
    this.mapView?.map.add(featureLayer, 0);
    
    // Navigate to the geometry extent
    this.mapView?.goTo(geometry);
    
    return featureLayer;
  };

  removeClaimedHarvestOriginLayer = () => {
    this.removeLayerById('claimed-harvest-origin-layer');
  };
}

export const mapControllerVerification = new MapControllerVerification();
